import React from 'react';
import { Column } from 'react-virtualized';
import { FormattedMessage, useIntl } from 'react-intl';
import TableStyles from 'styles/table.css';
import {
  EXTRA_DEFAULT_COLUMN_WIDTH,
  PATIENT_TABLE_COLUMN_HEADERS,
  TABLE_COLUMN_HEADER_WIDTH,
  TABLE_HEADER_HEIGHT_NO_FILTERS,
  TABLE_ROW_HEIGHT,
} from '../Table/utils';
import { DefaultRowRenderer } from '../Table/RowRenderers';
import {
  billingCodeCellRenderer,
  connectionPlanCellRenderer,
  connectionStatusCellRenderer,
  customColumnCellRenderer,
  dobCellRenderer,
  orderingProviderCellRenderer,
  patientCellRenderer,
} from '../Table/CellRenderers';
import PagingTable from '../Table/PagingTable';
import { checkSectionIsVisible } from '../../../legacy/containers/Forms/components/formValidations';
import { getTeamPatientFieldsConfig } from '../../../common/enrollmentUtils';
import {
  PATIENT_FIELD_NAMES,
  TABLE_COLUMN_VISIBILITY_LOOKUP_KEY as LOOKUP_KEY,
} from '../../../legacy/constants';
import { sortBy } from '../../../legacy/pages/PatientFile/util';
import { CONTENT_INNER_WIDTH } from '../../../common/constants';

const sortByDisplayName = sortBy('displayName');
/**
 * It generates column config for both default and custom fields for the patients table
 * @param {Fn} headerRenderer
 * @param {Object} intl
 * @param {Object} teamPatientFieldsConfig
 * @param {Array} visibleCustomFields
 * @returns
 */
const renderColumns = (
  headerRenderer,
  intl,
  teamPatientFieldsConfig,
  visibleCustomFields,
) => {
  let DEFAULT_COLUMN_CONFIG = [
    {
      cellRenderer: patientCellRenderer,
      width: TABLE_COLUMN_HEADER_WIDTH.PATIENT,
      intlKey: 'search.SearchResults.TableHeader.Patients',
      dataKey: PATIENT_TABLE_COLUMN_HEADERS.PATIENT,
      canShow: checkSectionIsVisible(
        teamPatientFieldsConfig,
        [
          PATIENT_FIELD_NAMES.FIRST_NAME,
          PATIENT_FIELD_NAMES.LAST_NAME,
          PATIENT_FIELD_NAMES.MRN,
        ],
        LOOKUP_KEY,
      ),
    },
    {
      cellRenderer: dobCellRenderer,
      width: TABLE_COLUMN_HEADER_WIDTH.DOB,
      intlKey: 'search.SearchResults.TableHeader.DOB',
      dataKey: PATIENT_TABLE_COLUMN_HEADERS.DOB,
      canShow: checkSectionIsVisible(
        teamPatientFieldsConfig,
        [PATIENT_FIELD_NAMES.DATE_OF_BIRTH],
        LOOKUP_KEY,
      ),
    },
    {
      cellRenderer: orderingProviderCellRenderer,
      width: TABLE_COLUMN_HEADER_WIDTH.ORDERING_PHYSICIAN,
      intlKey: 'search.SearchResults.TableHeader.Providers',
      dataKey: PATIENT_TABLE_COLUMN_HEADERS.ORDERING_PHYSICIAN,
      canShow: checkSectionIsVisible(
        teamPatientFieldsConfig,
        [PATIENT_FIELD_NAMES.ORDERING_PHYSICIAN],
        LOOKUP_KEY,
      ),
    },
    {
      cellRenderer: connectionStatusCellRenderer,
      width: TABLE_COLUMN_HEADER_WIDTH.CONNECTION_STATUS,
      intlKey: 'search.SearchResults.TableHeader.Connections',
      dataKey: PATIENT_TABLE_COLUMN_HEADERS.CONNECTION_STATUS,
      canShow: true,
    },
    {
      cellRenderer: connectionPlanCellRenderer,
      width: visibleCustomFields.length
        ? 280
        : TABLE_COLUMN_HEADER_WIDTH.PRESCRIPTION,
      intlKey: 'search.SearchResults.TableHeader.Prescriptions',
      dataKey: PATIENT_TABLE_COLUMN_HEADERS.PRESCRIPTION,
      canShow: true,
    },
    {
      cellRenderer: billingCodeCellRenderer,
      width: TABLE_COLUMN_HEADER_WIDTH.BILLING_CODE,
      intlKey: 'search.SearchResults.TableHeader.BillingCode',
      dataKey: PATIENT_TABLE_COLUMN_HEADERS.BILLING_CODE,
      canShow: true,
    },
    {
      cellRenderer: customColumnCellRenderer,
      width: checkSectionIsVisible(
        teamPatientFieldsConfig,
        [PATIENT_FIELD_NAMES.SUBJECT_ID],
        LOOKUP_KEY,
      )
        ? EXTRA_DEFAULT_COLUMN_WIDTH.SUBJECT_ID
        : 0,
      intlKey: 'label.patient_id',
      dataKey: PATIENT_TABLE_COLUMN_HEADERS.SUBJECT_ID,
      canShow: checkSectionIsVisible(
        teamPatientFieldsConfig,
        [PATIENT_FIELD_NAMES.SUBJECT_ID],
        LOOKUP_KEY,
      ),
    },
    {
      cellRenderer: customColumnCellRenderer,
      width: checkSectionIsVisible(
        teamPatientFieldsConfig,
        [PATIENT_FIELD_NAMES.STUDY_ID],
        LOOKUP_KEY,
      )
        ? EXTRA_DEFAULT_COLUMN_WIDTH.STUDY_ID
        : 0,
      intlKey: 'label.study_id',
      dataKey: PATIENT_TABLE_COLUMN_HEADERS.STUDY_ID,
      canShow: checkSectionIsVisible(
        teamPatientFieldsConfig,
        [PATIENT_FIELD_NAMES.STUDY_ID],
        LOOKUP_KEY,
      ),
    },
    {
      cellRenderer: customColumnCellRenderer,
      width: checkSectionIsVisible(
        teamPatientFieldsConfig,
        [PATIENT_FIELD_NAMES.SITE_IDENTIFICATION_NUMBER],
        LOOKUP_KEY,
      )
        ? EXTRA_DEFAULT_COLUMN_WIDTH.SITE_IDENTIFICATION_NUMBER
        : 0,
      intlKey: 'label.site_identification',
      dataKey: PATIENT_TABLE_COLUMN_HEADERS.SITE_IDENTIFICATION_NUMBER,
      canShow: checkSectionIsVisible(
        teamPatientFieldsConfig,
        [PATIENT_FIELD_NAMES.SITE_IDENTIFICATION_NUMBER],
        LOOKUP_KEY,
      ),
    },
  ].filter(config => config.canShow);

  const availableWidth =
    CONTENT_INNER_WIDTH -
    DEFAULT_COLUMN_CONFIG.reduce(
      (totalWidth, currentColumn) => totalWidth + currentColumn.width,
      0,
    );

  // if there are no custom fields and few default columns are hidden
  // then distribute the available width to all visible columns
  if (
    DEFAULT_COLUMN_CONFIG.length <
      Object.keys(TABLE_COLUMN_HEADER_WIDTH).length &&
    !visibleCustomFields.length
  ) {
    const availableWidthForEachColumn =
      availableWidth / DEFAULT_COLUMN_CONFIG.length;

    DEFAULT_COLUMN_CONFIG = DEFAULT_COLUMN_CONFIG.map(config => ({
      ...config,
      width: config.width + availableWidthForEachColumn,
    }));
  }

  // custom fields sort by display name in alphabetical order
  const CUSTOM_COLUMN_CONFIG = visibleCustomFields
    .sort(sortByDisplayName)
    .map(({ fieldName, displayName, id, isVisible }) => ({
      cellRenderer: customColumnCellRenderer,
      width: availableWidth / visibleCustomFields.length,
      intlKey: null, // no internationalization is required for custom fields
      dataKey: fieldName,
      id,
      canShow: isVisible,
      displayName,
    }));

  return [...DEFAULT_COLUMN_CONFIG, ...CUSTOM_COLUMN_CONFIG].map(
    ({ width, intlKey, cellRenderer, dataKey, displayName }) => (
      <Column
        key={dataKey}
        width={width}
        className={TableStyles.column}
        label={
          intlKey
            ? intl.formatMessage({
                id: intlKey,
              })
            : displayName
        }
        dataKey={dataKey} // not needed but field required by library
        headerRenderer={headerRenderer}
        cellRenderer={cellRenderer}
      />
    ),
  );
};

const SearchResultsTable = ({
  styles,
  noDataText = <FormattedMessage id="notfound.patient" />,
  isLoading,
  onRowClick,
  sortBy: sortByFn,
  sortDirection,
  scrollToIndex,
  loadingPage,
  onScroll,
  onRowsRendered,
  rows,
  error,
  headerRenderer,
  me,
}) => {
  const intl = useIntl();
  const { teams } = me.data;
  const { teamPatientFields } = (teams && teams[0]) || [];
  const visibleCustomFields = teamPatientFields.filter(
    field => field.isCustom && field[LOOKUP_KEY],
  );
  const teamPatientFieldsConfig = getTeamPatientFieldsConfig(teams);
  return (
    <PagingTable
      wrapperClassName={styles.patientSearchResultsTableWrapper}
      noDataText={noDataText}
      enableLoader
      showLoader={isLoading}
      headerHeight={TABLE_HEADER_HEIGHT_NO_FILTERS}
      rowHeight={TABLE_ROW_HEIGHT}
      onRowClick={onRowClick}
      rowRenderer={DefaultRowRenderer}
      error={error}
      rows={rows}
      sortBy={sortByFn}
      sortDirection={sortDirection}
      onRowsRendered={onRowsRendered}
      scrollToIndex={scrollToIndex}
      scrollToAlignment="start"
      onScroll={onScroll}
      loadingPage={loadingPage}
      me={me}
    >
      {renderColumns(
        headerRenderer,
        intl,
        teamPatientFieldsConfig,
        visibleCustomFields,
      )}
    </PagingTable>
  );
};
export default SearchResultsTable;



// WEBPACK FOOTER //
// ./src/views/components/Search/SearchResultsTable.jsx